import { useApolloClient } from '@apollo/react-hooks';
import { ApolloClient } from 'apollo-boost';
import compact from 'lodash/compact';
import pick from 'lodash/pick';
import { useReducer } from 'react';

import { PhotoItemsFragment, useWeddingWebsiteQuery, Wws_AlbumPhoto } from '../../generated/graphql';
import mediaApiReplace from './mediaApiReplace';
import mediaApiUpload from './mediaApiUpload';
import { ImageError } from './mediaApiUtils';
import { imgMaxSizePx, loadDataUrlToImg, readFileAsDataUrl } from './processImages';

export interface Image {
  id?: number | null;
  fileRef?: File;
  img?: HTMLImageElement;
  key: string;
  dataUrl?: string;
  error?: ImageError;
  uploadPercent?: number;
  mediaUrl?: string;
  mediaApiId?: string | null;
  height?: number;
  width?: number;
}

export interface ValidationOptions {
  minWidth: number;
  minHeight: number;
}

interface UseImageUploaderReturns {
  addImages(fileRefs: File[] | FileList, options?: AddImageOpts): Promise<Image[]>;
  images: Image[];
  errors?: Image[];
  imagesUploading: boolean;
  setExistingImages(existingImages: Array<Wws_AlbumPhoto | PhotoItemsFragment>): void;
}
interface UpdateProps {
  error?: ImageError;
  height?: number;
  mediaApiId?: string;
  mediaUrl?: string;
  width?: number;
  uploadPercent?: number;
}
interface State {
  images: Image[];
  errors: Image[];
}

type Action =
  | { type: 'UPDATE_IMAGE'; payload: UpdateProps & { key?: string } }
  | { type: 'ADD_IMAGES'; payload: Image[] };

export const getFileKey = (imageRef: File): string => `${imageRef.lastModified}|${imageRef.size}|${imageRef.type}`;

export const loadImageWithoutValidation = async (fileRef: File): Promise<Omit<Image, 'error'>> => {
  let dataUrl = await readFileAsDataUrl(fileRef);
  let img;

  if (typeof dataUrl === 'string') {
    if (!dataUrl.match(/data:image\/gif/)) {
      img = await loadDataUrlToImg(dataUrl);
      dataUrl = imgMaxSizePx(img, 2500);
    }
  }
  img = await loadDataUrlToImg(dataUrl);

  const result = {
    dataUrl: dataUrl || '',
    fileRef,
    height: img?.height,
    img,
    key: getFileKey(fileRef),
    width: img?.width,
  };

  return result;
};

export const loadImage = async (fileRef: File, options?: ValidationOptions): Promise<Image> => {
  const result = await loadImageWithoutValidation(fileRef);

  return {
    ...result,
    error: validateImage(result, options),
  };
};

const reducer = (state: State, action: Action): State => {
  switch (action.type) {
    case 'UPDATE_IMAGE':
      const { key, ...payload } = action.payload;
      let errors: Image[] = [...state.errors];
      const images = compact(
        state.images.map(i => {
          if (i.key !== key) {
            return i;
          }

          if (payload.error) {
            errors = [...errors, { ...i, error: payload.error }];
            return undefined;
          }

          return { ...i, ...payload };
        })
      );

      return { ...state, images, errors };

    case 'ADD_IMAGES':
      return { ...state, images: [...state.images, ...action.payload] };
    default:
      throw Error('not implemented');
  }
};

export const validateImage = (
  image: Image | HTMLImageElement,
  options: ValidationOptions = { minWidth: 500, minHeight: 500 }
) => {
  const { height, width } = image;
  const { minWidth, minHeight } = options;
  let error: ImageError | undefined;
  if ((width && width < minWidth) || (height && height < minHeight)) {
    error = { type: 'TOO_SMALL', message: 'This image is too small to display clearly on your website.' };
  }
  return error;
};

interface UploadImageProps {
  apolloClient: ApolloClient<object>;
  image: Image;
  memberId?: string;
  updateImage?(updates: any): void;
}
export const uploadImage = async (props: UploadImageProps): Promise<Image> => {
  const { apolloClient, image, memberId, updateImage } = props;
  const setUploadPercent = (percent: number) => {
    if (updateImage) {
      updateImage({ uploadPercent: percent });
    }
  };
  let update: UpdateProps = {};

  // Validate image
  update.error = validateImage(image);

  // Upload image
  if (!update?.error) {
    setUploadPercent(0);
    const response = await mediaApiUpload({ apolloClient, file: image.dataUrl, memberId, setUploadPercent });

    // Handle response
    const { data, error } = response;
    update = pick(data, ['height', 'id', 'mediaApiId', 'mediaApiUrl', 'width']);
    update = { ...update, error };
  }

  if (updateImage) {
    updateImage(update);
  }

  // Return result
  return {
    ...image,
    ...update,
  };
};

interface ImageUploadProps {
  file?: File | string;
  imageUrl?: string;
  setUploadPercent?(percent: number): void;
}

export const useImageUploader = () => {
  const apolloClient = useApolloClient();
  const { data } = useWeddingWebsiteQuery();
  const { member } = data || {};
  const { id: memberId } = member || {};

  const imageUpload = (
    { file, imageUrl, setUploadPercent }: ImageUploadProps,
    options: ValidationOptions = { minWidth: 500, minHeight: 500 }
  ) => {
    return mediaApiUpload({ apolloClient, file, imageUrl, memberId, setUploadPercent });
  };
  const imageReplace = (
    { file, mediaApiId }: { file: File; mediaApiId: string },
    options: ValidationOptions = { minWidth: 500, minHeight: 500 }
  ) => {
    if (memberId) {
      return mediaApiReplace({ apolloClient, file, mediaApiId, memberId });
    }
  };
  return { imageUpload, imageReplace };
};

interface AddImageOpts {
  upload?: boolean;
}
const useMultiImageUploader = (): UseImageUploaderReturns => {
  const apolloClient = useApolloClient();
  const { data } = useWeddingWebsiteQuery();
  const { member } = data || {};
  const { id: memberId } = member || {};
  const [{ images, errors }, dispatch] = useReducer<(state: State, action: Action) => State>(reducer, {
    images: [],
    errors: [],
  });

  const updateImage = (key: string) => (updates: any): void => {
    dispatch({ type: 'UPDATE_IMAGE', payload: { ...updates, key } });
  };

  const addImages = async (fileRefs: Array<File>, options: AddImageOpts): Promise<Image[]> => {
    const { upload = true } = options || {};
    let images;
    images = await Promise.all(fileRefs.map(f => loadImage(f))).then(image => {
      return image;
    });
    dispatch({ type: 'ADD_IMAGES', payload: images });
    if (upload) {
      images = await Promise.all(
        images.map(image => uploadImage({ apolloClient, image, memberId, updateImage: updateImage(image.key) }))
      );
    }
    return images;
  };

  const imagesUploading = images.some(i => !i.error && i.uploadPercent && i.uploadPercent < 100);

  const setExistingImages = (existingImages: Wws_AlbumPhoto[]) => {
    const imageIds = images.map(i => i.id);
    const updates = existingImages
      .filter(i => !imageIds.includes(i.id))
      .map(image => ({
        ...pick(image, ['id', 'mediaApiId', 'mediaUrl', 'rank']),
        dataUrl: image.mediaUrl,
        key: image.id?.toString() || Math.random().toString(),
        uploadPercent: 100, // Because they are already uploaded
      }));
    dispatch({ type: 'ADD_IMAGES', payload: updates });
  };

  return {
    addImages,
    images,
    imagesUploading,
    errors,
    setExistingImages,
  };
};

export default useMultiImageUploader;
